import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {

  CurrencyDollarIcon,
  ClockIcon,
  HeartIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalculatorIcon,
  TagIcon,
  TruckIcon,
  BellAlertIcon,
  BeakerIcon,
  ClipboardDocumentCheckIcon,
  MapIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { fetchBloodBankProfile, fetchBloodBankBookings } from '../../../services/MasterAdmin/bloodBankService';
import { toggleVendorStatus } from '../../../services/MasterAdmin/vendorService';
import { BloodBankProfile } from '../../../models/MasterAdmin/bloodBank';
import { BloodBankBookingsResponse, BloodBankBooking } from '../../../models/MasterAdmin/bloodBankBooking';
import FilePreview from '../../../components/MasterAdmin/FilePreview';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: string;
  description?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, trendValue, description }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {trendValue}
          </p>
        )}
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">{icon}</div>
    </div>
  </div>
);

const BloodBankVendorDashboard: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [bloodBankData, setBloodBankData] = useState<BloodBankProfile | null>(null);
  const [bookingData, setBookingData] = useState<BloodBankBookingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const loadData = async () => {
      if (!vendorId) return;
      
      try {
        setLoading(true);
        const [profileResponse, bookingsResponse] = await Promise.all([
          fetchBloodBankProfile(vendorId),
          fetchBloodBankBookings(vendorId)
        ]);
        setBloodBankData(profileResponse);
        setBookingData(bookingsResponse);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vendorId]);

  const handleToggleStatus = async (vendorId: string, currentStatus: string) => {
    try {
      setUpdatingStatus(vendorId);
      const response = await toggleVendorStatus(vendorId);
      if (response.success) {
        setBloodBankData((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            Vendor: {
              ...prev.Vendor,
              status: response.data.status as 'approved' | 'pending' | 'rejected'
            }
          };
        });
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again later.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const filteredBookings = bookingData?.bookings.filter((booking: BloodBankBooking) => {
    const matchesSearch = 
      booking.user.name.toLowerCase().includes(searchText.toLowerCase()) ||
      booking.bloodType.toLowerCase().includes(searchText.toLowerCase()) ||
      booking.bookingId.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }) || [];

  const paginatedBookings = filteredBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !bloodBankData) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Blood bank data not found'}</p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      {/* Header with Blood Bank Info */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
                <BeakerIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{bloodBankData.agencyName}</h1>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{bloodBankData.city}, {bloodBankData.state}</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <PhoneIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{bloodBankData.phoneNumber}</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <EnvelopeIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{bloodBankData.email}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center flex-wrap justify-end gap-2">
              <button
                onClick={() => setShowDetails(true)}
                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                title="View Details"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                bloodBankData.Vendor.status === 'approved'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : bloodBankData.Vendor.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {bloodBankData.Vendor.status.charAt(0).toUpperCase() + bloodBankData.Vendor.status.slice(1)}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                bloodBankData.Vendor.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {bloodBankData.Vendor.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">24/7 Service:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                bloodBankData.is24x7Operational
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {bloodBankData.is24x7Operational ? 'Available' : 'Not Available'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleToggleStatus(bloodBankData.Vendor.vendorId, bloodBankData.Vendor.status)}
                disabled={updatingStatus === bloodBankData.Vendor.vendorId}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                  bloodBankData.Vendor.status === 'approved'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {updatingStatus === bloodBankData.Vendor.vendorId ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    {bloodBankData.Vendor.status === 'approved' ? (
                      <>
                        <ClockIcon className="w-4 h-4" />
                        <span>Mark as Pending</span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Mark as Approve</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <button
              type="button"
              onClick={() => navigate(`/vendors/blood-bank/${vendorId}/event-timeline`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RectangleStackIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              Event timeline
            </button>
            <button
              type="button"
              onClick={() => navigate(`/vendors/blood-bank/${vendorId}/login-activity`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ShieldCheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Login activity
            </button>
          </div>
        </div>
      </div>

      {/* Store Details Modal */}
      {showDetails && bloodBankData && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center border-b dark:border-gray-700 p-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Blood Bank Details
                </h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  bloodBankData.Vendor.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : bloodBankData.Vendor.status === 'pending'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}>
                  {bloodBankData.Vendor.status.charAt(0).toUpperCase() + bloodBankData.Vendor.status.slice(1)}
                </span>
              </div>
              <button 
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-4rem)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Agency Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.agencyName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Owner Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.ownerName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Phone</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.phoneNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Website</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.website}</p>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Address Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Complete Address</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.completeAddress}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Landmark</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.nearbyLandmark}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">City</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.city}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">State</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.state}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Pincode</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.pincode}</p>
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Business Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">GST Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.gstNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">PAN Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.panNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Generated ID</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.generatedId}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Language Proficiency</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{bloodBankData.languageProficiency}</p>
                    </div>
                  </div>
                </div>

                {/* Services Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Services Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Blood Services</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {bloodBankData.bloodServicesProvided.map((service, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Platelet Services</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {bloodBankData.plateletServicesProvided.map((service, index) => (
                          <span key={index} className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Other Services</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {bloodBankData.otherServicesProvided.map((service, index) => (
                          <span key={index} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Documents and Photos */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Documents and Photos</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Agency Photos</label>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {bloodBankData.agencyPhotos.map((photo, index) => (
                          <FilePreview 
                            key={index}
                            url={photo.url}
                            name={photo.name}
                            className="w-full"
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">License Files</label>
                      <div className="mt-2 space-y-2">
                        {bloodBankData.licenseFiles.map((file, index) => (
                          <FilePreview 
                            key={index}
                            url={file.url}
                            name={file.name}
                            className="w-full"
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Registration Certificates</label>
                      <div className="mt-2 space-y-2">
                        {bloodBankData.registrationCertificateFiles.map((file, index) => (
                          <FilePreview 
                            key={index}
                            url={file.url}
                            name={file.name}
                            className="w-full"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Core Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Blood Requests"
          value={bookingData?.total || 0}
          icon={<HeartIcon className="h-6 w-6 text-red-600 dark:text-red-400" />}
          description="All time requests"
        />
        <MetricCard
          title="Completed Requests"
          value={bookingData?.completed || 0}
          icon={<CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
          description="Successfully fulfilled"
        />
        <MetricCard
          title="Pending Requests"
          value={bookingData?.pending || 0}
          icon={<ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
          description="Awaiting completion"
        />
        <MetricCard
          title="Cancelled Requests"
          value={bookingData?.cancelled || 0}
          icon={<XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />}
          description="Cancelled requests"
        />
      </div>

      {/* Bookings List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Blood Requests</h2>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search bookings..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 w-64"
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>

        {filteredBookings.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
              <ClipboardDocumentCheckIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Bookings Found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchText || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'There are no blood requests at the moment'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {paginatedBookings.map((booking: BloodBankBooking) => (
                <div 
                  key={booking.bookingId} 
                  className="p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-600"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <UserCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{booking.user.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">ID: {booking.bookingId}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                        booking.status === 'COMPLETED'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : booking.status === 'PENDING'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      }`}>
                        {booking.status}
                      </span>
                      <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                        booking.paymentStatus === 'PAID'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      }`}>
                        {booking.paymentStatus}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="flex items-center space-x-2">
                      <BeakerIcon className="h-5 w-5 text-red-500 dark:text-red-400" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Blood Type</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{booking.bloodType}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalculatorIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Units</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{booking.units}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <TruckIcon className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Delivery</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{booking.deliveryType}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CurrencyDollarIcon className="h-5 w-5 text-green-500 dark:text-green-400" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Amount</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">₹{booking.totalAmount}</p>
                      </div>
                    </div>
                  </div>
                  {booking.requestDetails.prescriptionUrls.length > 0 && (
                    <button className="mt-4 inline-flex items-center text-blue-600 dark:text-blue-400 text-sm hover:underline">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View Prescription
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {filteredBookings.length > itemsPerPage && (
              <div className="flex justify-between items-center mt-6 pt-6 border-t dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredBookings.length)} of {filteredBookings.length} requests
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.ceil(filteredBookings.length / itemsPerPage) }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredBookings.length / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(filteredBookings.length / itemsPerPage)}
                    className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Response Time Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Response Time Stats</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Average Response Time</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">1.5 mins</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                  <div className="h-2 bg-green-500 rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Call Handling Time</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">3.2 mins</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                  <div className="h-2 bg-blue-500 rounded-full" style={{ width: '78%' }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* Most Requested Blood Types */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Most Requested Blood Types</h2>
            <div className="space-y-4">
              {[
                { type: 'O+', requests: 450, units: '1,350' },
                { type: 'A+', requests: 380, units: '1,140' },
                { type: 'B+', requests: 320, units: '960' },
                { type: 'AB+', requests: 180, units: '540' },
                { type: 'O-', requests: 150, units: '450' },
              ].map((bloodType) => (
                <div key={bloodType.type} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Blood Type {bloodType.type}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{bloodType.requests} requests</p>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{bloodType.units} units</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Financial Overview */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Financial Overview</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">₹3.2M</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <CalculatorIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">GST Collected</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">₹4.8L</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <TagIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Delivery Fees</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">₹1.2L</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <ArrowUturnLeftIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Refunds</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">₹45K</span>
              </div>
            </div>
          </div>

          {/* Request Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Request Distribution</h2>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Delivery vs Pickup</h3>
                <div className="space-y-3">
                  {[
                    { type: 'Delivery', percentage: 75 },
                    { type: 'Pickup', percentage: 25 },
                  ].map((item) => (
                    <div key={item.type} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.type}</span>
                      <div className="flex items-center">
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                          <div 
                            className="h-2 bg-red-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Request Urgency</h3>
                <div className="space-y-3">
                  {[
                    { type: 'Emergency', percentage: 40 },
                    { type: 'Urgent', percentage: 35 },
                    { type: 'Regular', percentage: 25 },
                  ].map((item) => (
                    <div key={item.type} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.type}</span>
                      <div className="flex items-center">
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                          <div 
                            className="h-2 bg-red-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Geographic Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Geographic Distribution</h2>
            <div className="space-y-4">
              {[
                { area: 'South Mumbai', requests: 450 },
                { area: 'Western Suburbs', requests: 380 },
                { area: 'Eastern Suburbs', requests: 320 },
                { area: 'Central Mumbai', requests: 280 },
              ].map((area) => (
                <div key={area.area} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center">
                    <MapIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{area.area}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{area.requests} requests</span>
                </div>
              ))}
            </div>
          </div>

          {/* System Reminders */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">System Reminders</h2>
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                <div className="flex items-start">
                  <BellAlertIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Update Required
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                      Please update your blood inventory and availability status for Q2 2024
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                      Due by: March 31, 2024
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BloodBankVendorDashboard; 